'use client';

import type BigNumber from 'bignumber.js';

import { BaseDefiYieldOverviewResponse, ProcessingMintRequest } from '@flipster/apis/v1/dynamic-earn/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Badge } from '@flipster/react/design-system/badge';
import { Button } from '@flipster/react/design-system/button';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import InProgressIcon from '@flipster/react/icon/generated/InProgressIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { center, flex, grid, hstack, square, stack, vstack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { DynamicEarnEvent } from '@flipster/utils/segment/events/dynamic-earn';

import { CurrencyDisplay } from '../../../../misc/display';
import { getNumberColorWithNegativeWhite } from '../../../shared/color';
import { CrossMarginTooltipContent } from '../../../shared/cross-margin-tooltip-content';
import { CumulativeEarningsTooltipContent } from '../../../shared/cumulative-earnings-tooltip-content';
import { DynamicEarnProcessingModal } from '../../../shared/dynamic-earn-processing-modal';
import { UnclaimedEarningsTooltipContent } from '../../../shared/unclaimed-earnings-tooltip-content';

export interface ActiveCardProps {
  overview: BaseDefiYieldOverviewResponse | undefined;
  currency: string;
  productName: string;
  detailsPath: string;
  collateralRate: BigNumber | undefined;
  hasPendingRequest: boolean;
  hasRedeemAvailableBalance: boolean;
  onSubscribe: () => void;
  onRedeem: () => void;
  onAprInfoClick: () => void;
}

export const ActiveCard = ({
  overview,
  currency,
  productName,
  detailsPath,
  collateralRate,
  hasPendingRequest,
  hasRedeemAvailableBalance,
  onSubscribe,
  onRedeem,
  onAprInfoClick,
}: ActiveCardProps) => {
  const router = useRouter();

  return (
    <div className={css({ bg: 'background.default.level-1', rounded: 8, overflow: 'hidden' })}>
      <div
        className={stack({
          gap: 8,
          p: 24,
        })}
      >
        <div className={hstack({ justify: 'space-between' })}>
          <button type='button' onClick={() => router.push(detailsPath)} className={hstack()}>
            <CurrencyDisplay
              currency={currency}
              icon={<CurrencyDisplay.Icon size={32} />}
              mainLabel={<CurrencyDisplay.Label value='fullName' css={{ textStyle: 'Text/L/Regular' }} />}
              css={{ color: 'content.default.level-5' }}
            />
            <div className={square({ size: 24, color: 'content.default.level-3' })}>
              <ChevronRightIcon size={16} />
            </div>
          </button>
          {hasPendingRequest && (
            <Badge type='tint' color='neutral'>
              <FormattedMessage id='Common.r251204.Processing_status' />
            </Badge>
          )}
        </div>
        <div className={grid({ columns: { base: 1, tablet: 2 }, columnGap: { base: 0, tablet: 56 } })}>
          <div className={stack({ gap: 8 })}>
            <div
              className={vstack({
                flex: 1,
                justify: 'center',
                gap: 4,
                pb: 12,
              })}
            >
              <Tooltip.Root placement='right' maxWidth={280}>
                <Tooltip.TextTrigger label={<FormattedMessage id='Earn.r260212.Cumulative_earnings' />} />
                <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Earn.r260212.Cumulative_earnings' />}>
                  <CumulativeEarningsTooltipContent />
                </Tooltip.Content>
              </Tooltip.Root>
              <div className={hstack({ gap: 8 })}>
                <Formatted.Number
                  value={overview?.cumulativeEarnings}
                  withPlus
                  precision={UsdtPrecision}
                  toFixed
                  suffix={UsdtCurrency}
                  css={{
                    textStyle: 'Title/L/Monospace',
                    color: getNumberColorWithNegativeWhite(overview?.cumulativeEarnings),
                  }}
                />
              </div>
            </div>
            <div className={flex({ gap: 8 })}>
              <Button
                hierarchy='secondary'
                color='accent'
                size='large'
                label={<FormattedMessage id='Common.r251008.Redeem' />}
                css={{ flex: 1 }}
                disabled={!hasRedeemAvailableBalance}
                onClick={() => {
                  segment.track(DynamicEarnEvent.DynamicEarnRedeemClick, {
                    product_name: productName,
                    cumulative_earnings: overview?.cumulativeEarnings ?? '0',
                    apr: overview?.estimateApr ?? '0',
                  });
                  onRedeem();
                }}
              />
              <Button
                hierarchy='primary'
                color='accent'
                size='large'
                label={<FormattedMessage id='Earn.r260203.Earn_more' />}
                css={{ flex: 1 }}
                onClick={() => {
                  segment.track(DynamicEarnEvent.DynamicEarnSubscribeClick, {
                    product_name: productName,
                    cumulative_earnings: overview?.cumulativeEarnings ?? '0',
                    apr: overview?.estimateApr ?? '0',
                  });
                  onSubscribe();
                }}
              />
            </div>
          </div>

          <div className={stack()}>
            <div
              className={stack({
                gap: 6,
                py: 10,
                tablet: {
                  borderTopWidth: '1px',
                  borderTopStyle: 'solid',
                  borderColor: 'border.default.level-1',
                },
              })}
            >
              <div className={hstack({ gap: 8, justify: 'space-between' })}>
                <Tooltip.Root placement='right' maxWidth={280}>
                  <Tooltip.TextTrigger label={<FormattedMessage id='Autogen_common.r251126.s_Subscribed_balance' />} />
                  <Tooltip.Content
                    bottomSheetTitle={<FormattedMessage id='Autogen_common.r251126.s_Subscribed_balance' />}
                  >
                    <FormattedMessage id='Earn.r251204.Subscribed_balance_desc' />
                  </Tooltip.Content>
                </Tooltip.Root>
                <span
                  className={css({
                    textStyle: 'Text/M/Monospace/Regular',
                    color: 'content.default.level-5',
                  })}
                >
                  <Formatted.Number
                    value={overview?.subscribedBalance}
                    suffix={UsdtCurrency}
                    precision={UsdtPrecision}
                    toFixed
                  />
                </span>
              </div>
              <div className={hstack({ gap: 8, justify: 'space-between' })}>
                <Tooltip.Root placement='right' maxWidth={280}>
                  <Tooltip.TextTrigger label={<FormattedMessage id='Earn.r260203.Available_for_cross_margin' />} />
                  <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Earn.r260203.Available_for_cross_margin' />}>
                    <CrossMarginTooltipContent collateralRate={collateralRate} />
                  </Tooltip.Content>
                </Tooltip.Root>
                <span
                  className={css({
                    textStyle: 'Text/M/Monospace/Regular',
                  })}
                >
                  <Formatted.Number
                    value={overview?.availableForCrossMargin}
                    suffix={UsdtCurrency}
                    precision={UsdtPrecision}
                    toFixed
                  />
                </span>
              </div>
            </div>
            <div
              className={stack({
                gap: 6,
                py: 10,
                borderTopWidth: '1px',
                borderTopStyle: 'solid',
                borderColor: 'border.default.level-1',
              })}
            >
              <div className={hstack({ gap: 8, justify: 'space-between' })}>
                <Tooltip.Root placement='right' maxWidth={280}>
                  <Tooltip.TextTrigger label={<FormattedMessage id='Autogen_common.r251126.s_Unclaimed_earnings' />} />
                  <Tooltip.Content
                    bottomSheetTitle={<FormattedMessage id='Autogen_common.r251126.s_Unclaimed_earnings' />}
                  >
                    <UnclaimedEarningsTooltipContent />
                  </Tooltip.Content>
                </Tooltip.Root>
                <span
                  className={css({
                    textStyle: 'Text/M/Monospace/Regular',
                  })}
                >
                  <Formatted.Number
                    value={overview?.unclaimedEarnings}
                    suffix={UsdtCurrency}
                    precision={UsdtPrecision}
                    withColor
                    withPlus
                    toFixed
                  />
                </span>
              </div>
              <div className={hstack({ gap: 8, justify: 'space-between' })}>
                <Tooltip.Root placement='right' maxWidth={280}>
                  <Tooltip.TextTrigger label={<FormattedMessage id='Autogen_common.r251126.s_Claimed_earnings' />} />
                  <Tooltip.Content
                    bottomSheetTitle={<FormattedMessage id='Autogen_common.r251126.s_Claimed_earnings' />}
                  >
                    <FormattedMessage id='Earn.r251204.Claimed_earnings_desc' />
                  </Tooltip.Content>
                </Tooltip.Root>
                <span
                  className={css({
                    textStyle: 'Text/M/Monospace/Regular',
                  })}
                >
                  <Formatted.Number
                    value={overview?.claimedEarnings}
                    suffix={UsdtCurrency}
                    precision={UsdtPrecision}
                    withColor
                    withPlus
                    toFixed
                  />
                </span>
              </div>
              <div className={hstack({ gap: 8, justify: 'space-between' })}>
                <div className={hstack({ gap: 2 })}>
                  <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                    <FormattedMessage id='Common.r251209.Apr' />
                  </p>
                  <button
                    type='button'
                    className={square({ size: 24, color: 'content.default.level-1' })}
                    onClick={onAprInfoClick}
                  >
                    <InformationCircleIcon size={16} />
                  </button>
                </div>
                <div className={hstack({ gap: 8, justify: 'space-between' })}>
                  <Formatted.Percent
                    key='percent'
                    value={overview?.estimateApr}
                    isRate
                    precision={2}
                    toFixed
                    css={{
                      textStyle: 'Text/M/Monospace/Regular',
                      color: 'content.default.level-5',
                      textAlign: 'right',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {hasPendingRequest && <Processing requests={overview?.processingMintRequests ?? []} />}
    </div>
  );
};

const Processing = ({ requests }: { requests: ProcessingMintRequest[] }) => {
  const overlay = useOverlay();

  return (
    <button
      className={center({
        py: 12,
        px: 16,
        bg: 'background.accent.primary.tint',
        w: '100%',
      })}
      type='button'
      onClick={() =>
        overlay.open(({ isOpen, close, unmount }) => (
          <DynamicEarnProcessingModal isOpen={isOpen} onClose={close} pendingRequests={requests} onExit={unmount} />
        ))
      }
    >
      <div
        className={hstack({
          gap: 8,
          color: 'content.default.level-5',
        })}
      >
        <InProgressIcon size={20} />
        <div className={hstack()}>
          <p
            className={css({
              textStyle: 'Text/M/Regular',
            })}
          >
            <FormattedMessage id='Common.r251204.Count_processing_status' values={{ count: requests.length }} />
          </p>
          <div className={square({ size: 24, color: 'content.default.level-3' })}>
            <ChevronRightIcon size={16} />
          </div>
        </div>
      </div>
    </button>
  );
};
